import { useEffect } from "react";
import { useLocation } from "wouter";

export interface TelegramWebAppUser {
  id: number;
  username?: string;
  language_code?: string;
}

export interface TelegramWebApp {
  ready(): void;
  expand(): void;
  initData: string;
  initDataUnsafe: { user?: TelegramWebAppUser };
  platform: string;
  colorScheme: "light" | "dark";
  BackButton: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  onEvent(event: "themeChanged", cb: () => void): void;
  offEvent(event: "themeChanged", cb: () => void): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

export function getTelegramWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

// Ключи платформ в конфиге панели — ios/android/macos/windows/linux (см. sub-page/src/common/
// platform-detection.util.ts). Telegram.WebApp.platform различает ios/android/macos напрямую;
// для остальных desktop-клиентов (tdesktop/weba/web/webk/unigram) точной ОС не даёт — добираем
// тем же паттерном, что и sub-page, по User-Agent.
export function detectPlatformKey(): string | null {
  const platform = getTelegramWebApp()?.platform;
  if (platform === "ios") return "ios";
  if (platform === "android" || platform === "android_x") return "android";
  if (platform === "macos") return "macos";

  const ua = navigator.userAgent;
  if (/Windows/.test(ua)) return "windows";
  if (/Linux/.test(ua)) return "linux";
  if (/Macintosh/.test(ua)) return "macos";
  return null;
}

// Переключателя темы для пользователя нет — тема ставится по Telegram.WebApp.colorScheme,
// вне Telegram (обычный браузер при разработке) фолбэк на "dark".
function applyTheme(): void {
  const scheme = getTelegramWebApp()?.colorScheme ?? "dark";
  document.documentElement.dataset.theme = scheme;
}

export function useTelegramTheme(): void {
  useEffect(() => {
    applyTheme();
    const tg = getTelegramWebApp();
    if (!tg) return;
    tg.onEvent("themeChanged", applyTheme);
    return () => tg.offEvent("themeChanged", applyTheme);
  }, []);
}

// Плоское трёхэкранное приложение: "назад" всегда ведёт на главную.
export function useTelegramBackButton(): void {
  const [location, navigate] = useLocation();

  useEffect(() => {
    const tg = getTelegramWebApp();
    if (!tg) return;

    if (location === "/") {
      tg.BackButton.hide();
      return;
    }

    const onClick = () => navigate("/");
    tg.BackButton.show();
    tg.BackButton.onClick(onClick);
    return () => tg.BackButton.offClick(onClick);
  }, [location, navigate]);
}
